import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import { DEFAULT_TEMPLATE_FILTERS } from '../utils/templateConstants';
import {
  applyTemplateOrganisationScope,
  lockTemplateOrganisationForUser,
} from '../utils/templateFilterUtils';
import { templateFiltersAtom, templateListRefreshAtom } from '../state/templateAtoms';
import { templateListQuery } from '../selectors/templateSelectors';
import { templateService } from '../services/templateService';

const EMPTY_LIST = [];

export default function useTemplateList() {
  const loggedInUser = useRecoilValue(userInformationAtom);
  const [filters, setFilters] = useRecoilState(templateFiltersAtom);
  const setRefresh = useSetRecoilState(templateListRefreshAtom);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const cacheRef = useRef({ templates: EMPTY_LIST, count: 0, error: null });
  const scopedFilters = useMemo(
    () => applyTemplateOrganisationScope(filters, loggedInUser),
    [filters, loggedInUser]
  );

  const loadable = useRecoilValueLoadable(templateListQuery(scopedFilters));
  const isLoading = loadable.state === 'loading';

  if (loadable.state === 'hasValue') {
    cacheRef.current = {
      templates: loadable.contents?.templates ?? EMPTY_LIST,
      count: loadable.contents?.count ?? 0,
      error: loadable.contents?.error ?? null,
    };
  }

  const templates =
    loadable.state === 'hasValue'
      ? loadable.contents?.templates ?? EMPTY_LIST
      : cacheRef.current.templates;
  const count =
    loadable.state === 'hasValue' ? loadable.contents?.count ?? 0 : cacheRef.current.count;
  const error =
    loadable.state === 'hasError'
      ? loadable.contents?.message || 'Failed to fetch templates'
      : loadable.state === 'hasValue'
        ? loadable.contents?.error ?? null
        : cacheRef.current.error;

  useEffect(() => {
    setFilters((prev) => {
      const next = applyTemplateOrganisationScope(prev, loggedInUser);
      if (next.organisation === prev.organisation) {
        return prev;
      }
      return next;
    });
  }, [loggedInUser, setFilters]);

  const updateFilters = useCallback(
    (patch) => {
      setFilters((prev) => lockTemplateOrganisationForUser({ ...prev, ...patch }, loggedInUser));
    },
    [loggedInUser, setFilters]
  );

  const resetFilters = useCallback(() => {
    setFilters(lockTemplateOrganisationForUser({ ...DEFAULT_TEMPLATE_FILTERS }, loggedInUser));
  }, [loggedInUser, setFilters]);

  const refreshList = useCallback(() => {
    setIsRefreshing(true);
    setRefresh((n) => n + 1);
    setTimeout(() => setIsRefreshing(false), 300);
  }, [setRefresh]);

  const syncFromProvider = useCallback(
    async ({ botNumber, companyId } = {}) => {
      setIsSyncing(true);
      setSyncMessage(null);
      try {
        const res = await templateService.syncTemplates({ botNumber, companyId });
        if (res?.status === 200 && res?.data?.success !== false) {
          const payload = res.data;
          const updated = payload?.updated ?? payload?.synced ?? 0;
          const skipped = payload?.skipped ?? 0;
          const skippedSuffix = skipped > 0 ? ` (${skipped} not in Machadalo)` : '';
          setSyncMessage({
            type: 'success',
            text: `Updated status for ${updated} templates${skippedSuffix}`,
          });
          if (payload?.last_synced_at) {
            setLastSyncedAt(payload.last_synced_at);
          } else {
            setLastSyncedAt(new Date().toISOString());
          }
          setRefresh((n) => n + 1);
          return { success: true, data: payload };
        }
        const errorText =
          payloadErrorMessage(res?.data) || 'Failed to sync templates from provider';
        setSyncMessage({ type: 'error', text: errorText });
        return { success: false, error: errorText };
      } catch (error) {
        const errorText = error?.message || 'Failed to sync templates from provider';
        setSyncMessage({ type: 'error', text: errorText });
        return { success: false, error: errorText };
      } finally {
        setIsSyncing(false);
      }
    },
    [setRefresh]
  );

  return {
    filters,
    setFilters,
    updateFilters,
    resetFilters,
    templates,
    count,
    error,
    isLoading,
    isRefreshing,
    isSyncing,
    syncMessage,
    lastSyncedAt,
    refreshList,
    syncFromProvider,
  };
}

function payloadErrorMessage(data) {
  if (!data) return null;
  if (typeof data === 'string') return data;
  if (typeof data.error === 'string') return data.error;
  if (data.error?.message) return data.error.message;
  if (Array.isArray(data.errors) && data.errors.length) {
    const first = data.errors[0];
    return typeof first === 'string' ? first : first?.error?.message || JSON.stringify(first);
  }
  return data.message || null;
}
